import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { 
  UserGroupIcon,
  AcademicCapIcon,
  CalendarIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  IdentificationIcon
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../../components/utils/ResourceError";
import RoleOnly, { useUserHasAccess } from "../../../../../routes/roleOnly";
import { assignCompetencyToEmployee } from "../../../../../api/hse/competency";
import { getCompetencyDefinitions } from "../../../../../api/hse/competency";
import { fetchUser } from "../../../../../api/users";

/**
 * HSE/KS Competency Employee Assign - Assign competency to employee
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyEmployeeAssign = () => {
  const { t } = useTranslation();
  const { id: employeeId } = useParams();
  const navigate = useNavigate();
  
  const [employee, setEmployee] = useState(null);
  const [competencyDefinitions, setCompetencyDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [formData, setFormData] = useState({
    competency_definition_id: "",
    issued_at: "",
    expires_at: "",
    certificate_number: "",
    notes: "",
  });

  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: "create" });

  useEffect(() => {
    if (canManage) {
      fetchEmployee();
      fetchCompetencyDefinitions();
    }
  }, [employeeId, canManage]);

  const fetchEmployee = async () => {
    try {
      const userResponse = await fetchUser(employeeId);
      setEmployee({
        id: employeeId,
        name: userResponse.data?.user?.name || "",
        email: userResponse.data?.user?.email || "",
      });
    } catch (err) {
      console.error("Error fetching employee:", err);
      setError(true);
      toast.error(t("Kunne ikke laste ansatt"));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetencyDefinitions = async () => {
    try {
      const response = await getCompetencyDefinitions({ is_active: true });
      setCompetencyDefinitions(response.competency_definitions || response.definitions || []);
    } catch (err) {
      console.error("Error fetching competency definitions:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.competency_definition_id) {
      toast.error(t("Kompetanse er påkrevd"));
      return;
    }

    try {
      setSaving(true);
      
      await assignCompetencyToEmployee(employeeId, formData);
      toast.success(t("Kompetanse tildelt"));
      navigate(`/dashboard/hse-ks/competency/employees/${employeeId}`);
    } catch (err) {
      console.error("Error assigning competency:", err);
      const errorMessage = err.response?.data?.error || err.message || t("Kunne ikke tildele kompetanse");
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    navigate(`/dashboard/hse-ks/competency/employees/${employeeId}`);
    return null;
  }

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste ansatt")]} />;
  }

  if (!employee) {
    return null;
  }

  return (
    <DashboardHeader
      title={t("Tildel kompetanse")}
      subtitle={employee.name || employee.email}
    >
      <div className="mx-auto mt-8 max-w-4xl sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Back Button */}
          <div>
            <button
              onClick={() => navigate(`/dashboard/hse-ks/competency/employees/${employeeId}`)}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              {t("Tilbake")}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Competency Selection */}
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Velg kompetanse")}</h3>
                
                <div>
                  <label htmlFor="competency_definition_id" className="block text-sm font-medium text-gray-700">
                    {t("Kompetanse")} <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="competency_definition_id"
                    id="competency_definition_id"
                    required
                    value={formData.competency_definition_id}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="">{t("Velg kompetanse")}</option>
                    {competencyDefinitions.map((def) => (
                      <option key={def.id} value={def.id}>
                        {def.name} {def.type && `(${def.type})`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Datoer")}</h3>
                
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* Issued Date */}
                  <div>
                    <label htmlFor="issued_at" className="block text-sm font-medium text-gray-700">
                      {t("Utstedt dato")}
                    </label>
                    <input
                      type="date"
                      name="issued_at"
                      id="issued_at"
                      value={formData.issued_at}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  {/* Expires Date */}
                  <div>
                    <label htmlFor="expires_at" className="block text-sm font-medium text-gray-700">
                      {t("Utløper dato")}
                    </label>
                    <input
                      type="date"
                      name="expires_at"
                      id="expires_at"
                      value={formData.expires_at}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {t("La stå tomt for permanent kompetanse")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Tilleggsinformasjon")}</h3>
                
                <div className="space-y-6">
                  {/* Certificate Number */}
                  <div>
                    <label htmlFor="certificate_number" className="block text-sm font-medium text-gray-700">
                      {t("Sertifikatnummer")}
                    </label>
                    <input
                      type="text"
                      name="certificate_number"
                      id="certificate_number"
                      value={formData.certificate_number}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder={t("F.eks. CERT-2024-001")}
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                      {t("Notater")}
                    </label>
                    <textarea
                      name="notes"
                      id="notes"
                      rows={4}
                      value={formData.notes}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder={t("Legg til notater...")}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate(`/dashboard/hse-ks/competency/employees/${employeeId}`)}
                className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {t("Avbryt")}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {saving ? t("Tildeler...") : t("Tildel kompetanse")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardHeader>
  );
};

